import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';

/**
 * The browser Maps JavaScript API key. Referrer-restricted in GCP to `*.luggagehero.com`,
 * `*.devheroo.com` and `https://localhost:*` (AM-4515), which is the boundary, so it is committed.
 */
const WEB_MAPS_API_KEY = 'AIzaSyDaBIVCBZqIPmJbxr3U-ub_S-DCs3zOT0g';

/** The global the Maps API calls once the SDK and its libraries are ready. */
export const GOOGLE_MAPS_CALLBACK = '__lhGoogleMapsLoaded';

/** The same request web-traveler's index.html made before AM-5110, with a callback to know when it is ready. */
export const GOOGLE_MAPS_JS_URL =
  `https://maps.googleapis.com/maps/api/js?v=quarterly&key=${WEB_MAPS_API_KEY}` +
  `&libraries=places&callback=${GOOGLE_MAPS_CALLBACK}`;

/** How long one caller waits. The script keeps loading after a timeout; a later call waits for the same one. */
export const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 60_000;

type MapsWindow = Window & { google?: { maps?: { Map?: unknown } }; [GOOGLE_MAPS_CALLBACK]?: () => void };

/**
 * Loads the Google Maps JavaScript API on demand, once.
 *
 * web-traveler used to load the SDK with a render-blocking `<script>` on every page, so pages with no map
 * (the booking page) waited for 375 KB they never used (AM-5110). Pages with a map now load it through
 * {@link googleMapsLoadedGuard}. Where the SDK is already on the page (ionic-traveler keeps its tag),
 * {@link load} resolves without adding another.
 */
@Injectable({
  providedIn: 'root',
})
export class SharedGoogleMapsLoaderService {
  private readonly document = inject(DOCUMENT);
  private readonly log = inject(SharedLoggingService);

  /** The one in-flight script load; cleared when it fails, so the next call retries. */
  private script: Promise<void> | null = null;

  public get isLoaded(): boolean {
    return this.window?.google?.maps?.Map != null;
  }

  public load(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }
    this.script ??= this.addScript().catch((err: unknown) => {
      this.script = null;
      throw err;
    });
    return withTimeout(this.script, GOOGLE_MAPS_LOAD_TIMEOUT_MS);
  }

  private get window(): MapsWindow | null {
    return this.document.defaultView as MapsWindow | null;
  }

  private addScript(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const window = this.window;
      if (!window) {
        reject(new Error('No window to load Google Maps into'));
        return;
      }

      const script = this.document.createElement('script');
      window[GOOGLE_MAPS_CALLBACK] = () => resolve();
      script.src = GOOGLE_MAPS_JS_URL;
      script.async = true;
      script.onerror = () => {
        script.remove();
        void this.log.error('Error loading Google Maps');
        reject(new Error('Error loading Google Maps'));
      };
      this.document.head.appendChild(script);
    });
  }
}

/**
 * Activates a route only once the Maps SDK is loaded, for routes whose components read `google.maps` while
 * they are built. A failed or timed-out load is logged and the route still activates, so navigation never
 * hangs on a third party.
 */
export const googleMapsLoadedGuard: CanActivateFn = async () => {
  const loader = inject(SharedGoogleMapsLoaderService);
  const log = inject(SharedLoggingService);
  try {
    await loader.load();
  } catch (err) {
    void log.error('Opening a map page without Google Maps', err);
  }
  return true;
};

function withTimeout(task: Promise<void>, ms: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Google Maps did not load within ${ms} ms`)), ms);
    task.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (err: Error) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
